// ── Trending Feed Score Calculation ───────────────────────────────
//
// Lightweight, transparent engagement scoring for the BCH community.
// NOT pay-to-win — rewards quality content and broad community support.
//
// Formula:
//   raw_score = (unique_tippers * 5) + (replies * 3) + (reposts * 2)
//              + (reactions * 1) + (boosts * 4) + min(total_tip_sats / 1000, 10)
//
// Time decay (Hacker News style):
//   final_score = raw_score / (hours_since_post + 2)
//
// Anti-abuse:
//   - Self-tips (tipper === post author) count zero
//   - Only tips >= 500 sats count toward unique tipper score (sybil defense)
//   - Boost influence capped at 4 (already pinned at top separately)
//   - Minimum score of 5 (before decay) to appear in trending
//   - Hard 48h cutoff — older posts don't appear regardless of score

import type { Event } from 'nostr-tools';
import { feedRankingMultiplier, type TrustLevel } from './trustScore';

// ── Configuration ────────────────────────────────────────────────

const WEIGHTS = {
  uniqueTipper: 5,   // Each unique person who tipped (sybil-resistant)
  reply: 3,          // Replies indicate discussion
  repost: 2,         // Reposts = amplification
  reaction: 1,       // Likes/reactions = lightweight engagement
  boost: 4,          // Paid boost (lower than spec's 8 — already gets pinned)
  tipSatsCap: 10,    // Max points from raw tip amount
  tipSatsDivisor: 1000, // tip_sats / 1000, capped at tipSatsCap
} as const;

const MIN_TIP_SATS = 500;       // Minimum tip to count as a unique tipper
const MIN_SCORE = 5;             // Minimum raw score to appear in trending
const MAX_AGE_HOURS = 48;        // Hard cutoff — posts older than this are excluded
const TIME_DECAY_OFFSET = 2;     // +2 prevents division by zero, smooths new posts

// ── Types ────────────────────────────────────────────────────────

export interface TrendingScore {
  eventId: string;
  rawScore: number;
  finalScore: number;
  // Breakdown for transparency badges
  uniqueTippers: number;
  replyCount: number;
  repostCount: number;
  reactionCount: number;
  boostCount: number;
  totalTipSats: number;
  hoursAge: number;
  // Primary trending reason
  reason: TrendingReason;
  // How many engagement events happened in the last hour (for "hot right now" indicator)
  recentEngagementCount: number;
}

export type TrendingReason =
  | 'supporters'    // High unique tipper count
  | 'most-replied'  // High reply count
  | 'community'     // Broad engagement across categories
  | 'trending';     // Generic trending

// ── Score Calculation ────────────────────────────────────────────

export interface EngagementData {
  /** All kind 7, 6, 1 events referencing this post */
  reactions: Event[];
  /** All kind 9735 tip receipts referencing this post */
  tipReceipts: Event[];
  /** Whether this post has an active boost */
  isBoosted: boolean;
  /** The post author's pubkey (for self-tip detection) */
  authorPubkey: string;
  /** Post created_at timestamp */
  createdAt: number;
  /** Optional trust level of the post author (for feed ranking adjustment) */
  authorTrustLevel?: TrustLevel;
}

export function calculateTrendingScore(data: EngagementData): TrendingScore | null {
  const now = Math.floor(Date.now() / 1000);
  const ageSeconds = now - data.createdAt;
  const hoursAge = ageSeconds / 3600;

  // Hard 48h cutoff
  if (hoursAge > MAX_AGE_HOURS) return null;

  // Count engagement types from reactions array
  const replyCount = data.reactions.filter(e => e.kind === 1).length;
  const repostCount = data.reactions.filter(e => e.kind === 6).length;
  // Deduplicate reactions by pubkey (one reaction per person)
  const uniqueReactors = new Set(data.reactions.filter(e => e.kind === 7).map(e => e.pubkey));
  const reactionCount = uniqueReactors.size;

  // Tip analysis with anti-abuse
  let totalTipSats = 0;
  const uniqueTipperSet = new Set<string>();

  for (const tip of data.tipReceipts) {
    // Self-tips count ZERO
    if (tip.pubkey === data.authorPubkey) continue;

    const amountTag = tip.tags.find(t => t[0] === 'amount');
    const sats = amountTag ? parseInt(amountTag[1]) || 0 : 0;

    totalTipSats += sats;

    // Only count as unique tipper if >= MIN_TIP_SATS (sybil defense)
    if (sats >= MIN_TIP_SATS) {
      uniqueTipperSet.add(tip.pubkey);
    }
  }
  const uniqueTippers = uniqueTipperSet.size;

  // Boost score (0 or 1, weighted)
  const boostCount = data.isBoosted ? 1 : 0;

  // Raw score calculation
  const rawScore =
    (uniqueTippers * WEIGHTS.uniqueTipper) +
    (replyCount * WEIGHTS.reply) +
    (repostCount * WEIGHTS.repost) +
    (reactionCount * WEIGHTS.reaction) +
    (boostCount * WEIGHTS.boost) +
    Math.min(totalTipSats / WEIGHTS.tipSatsDivisor, WEIGHTS.tipSatsCap);

  // Minimum score threshold
  if (rawScore < MIN_SCORE) return null;

  // Trust-level feed ranking adjustment
  // New accounts get a small penalty, trusted accounts get a small boost.
  // This nudges ranking without shadowbanning.
  const trustMultiplier = data.authorTrustLevel !== undefined
    ? feedRankingMultiplier(data.authorTrustLevel)
    : 1.0;

  // Time decay
  const finalScore = (rawScore * trustMultiplier) / (hoursAge + TIME_DECAY_OFFSET);

  // Count engagement events from the last hour ("hot right now" indicator)
  const oneHourAgo = now - 3600;
  let recentEngagementCount = 0;
  for (const ev of data.reactions) {
    if (ev.created_at >= oneHourAgo) recentEngagementCount++;
  }
  for (const tip of data.tipReceipts) {
    if (tip.created_at >= oneHourAgo) recentEngagementCount++;
  }

  // Determine primary trending reason
  let reason: TrendingReason = 'trending';
  const tipperScore = uniqueTippers * WEIGHTS.uniqueTipper;
  const replyScore = replyCount * WEIGHTS.reply;

  if (tipperScore >= replyScore && uniqueTippers >= 3) {
    reason = 'supporters';
  } else if (replyScore >= tipperScore && replyCount >= 3) {
    reason = 'most-replied';
  } else if (uniqueTippers >= 2 && replyCount >= 2 && reactionCount >= 2) {
    reason = 'community';
  }

  return {
    eventId: '', // filled in by caller
    rawScore,
    finalScore,
    uniqueTippers,
    replyCount,
    repostCount,
    reactionCount,
    boostCount,
    totalTipSats,
    hoursAge,
    reason,
    recentEngagementCount,
  };
}

// ── Reason Labels ────────────────────────────────────────────────

export function getTrendingReasonLabel(score: TrendingScore): string {
  switch (score.reason) {
    case 'supporters':
      return `${score.uniqueTippers} supporter${score.uniqueTippers !== 1 ? 's' : ''}`;
    case 'most-replied':
      return `${score.replyCount} repl${score.replyCount !== 1 ? 'ies' : 'y'}`;
    case 'community':
      return 'Community pick';
    case 'trending':
    default:
      return 'Trending in BCH';
  }
}

export function getTrendingReasonEmoji(reason: TrendingReason): string {
  switch (reason) {
    case 'supporters': return '\u{1F4B0}';
    case 'most-replied': return '\u{1F4AC}';
    case 'community': return '\u{1F31F}';
    case 'trending':
    default: return '\u{1F525}';
  }
}

